// src/components/Upload/UploadManager.tsx (With Demo Samples)
import React, { useCallback, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Description as FileIcon,
  Science as DemoIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  AutoAwesome as MagicWandIcon,
  Visibility as PreviewIcon,
  PictureAsPdf as PdfIcon,
  PlayArrow as RunIcon,
  FactCheck as ValidateIcon,
} from "@mui/icons-material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

interface UploadFile {
  id: string;
  file: File;
  status: "pending" | "ready" | "error";
  error?: string;
}

interface UploadManagerProps {
  files: UploadFile[];
  onFilesAdded: (files: File[], replace?: boolean) => void;
  onFileRemove: (id: string) => void;
  onSubmit: () => void;
  isUploading: boolean;
  error?: string | null;
}

interface DemoSample {
  id: string;
  name: string;
  description: string;
  category: string;
  policyDetails: {
    insured: string;
    location: string;
    policyNumber: string;
    effectiveDate: string;
    program: string;
    employees: number;
    coverages: {
      generalLiability: string;
      property: string;
    };
  };
  claimsSummary: {
    totalClaims: number;
    totalIncurred: string;
    lossRatio: string;
    claimType: string;
    description: string;
  };
  files: {
    pdf: string;
    pdfFilename: string;
    json: string;
    jsonFilename: string;
  };
}

const UploadManager: React.FC<UploadManagerProps> = ({
  files,
  onFilesAdded,
  onFileRemove,
  onSubmit,
  isUploading,
  error,
}) => {
  const [demoDialogOpen, setDemoDialogOpen] = useState(false);
  const [samples, setSamples] = useState<DemoSample[]>([]);
  const [selectedSample, setSelectedSample] = useState<DemoSample | null>(null);
  const [batchConfig, setBatchConfig] = useState<any>(null);

  // Data Quality Validation states
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [validationResult, setValidationResult] = useState<string>("");
  const [validatingFileId, setValidatingFileId] = useState<string | null>(null);

  // File Preview states
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<UploadFile | null>(null);
  const [previewContent, setPreviewContent] = useState<any>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Load demo samples and batch config
  useEffect(() => {
    fetch("/samples-config.json")
      .then((res) => res.json())
      .then((data) => {
        setSamples(data.samples);
        setBatchConfig(data.batchMode);
      })
      .catch((err) => console.error("Failed to load demo samples:", err));
  }, []);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      onFilesAdded(acceptedFiles);
    },
    [onFilesAdded],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      "application/json": [".json"],
    },
    disabled: isUploading,
  });

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const handleLoadSample = async (sample: DemoSample) => {
    try {
      console.log("ðŸ”µ Loading sample:", sample.name);
      console.log("ðŸ”µ PDF URL:", sample.files.pdf);
      console.log("ðŸ”µ JSON URL:", sample.files.json);

      // Fetch both files
      const [pdfResponse, jsonResponse] = await Promise.all([
        fetch(sample.files.pdf),
        fetch(sample.files.json),
      ]);

      console.log("ðŸ”µ PDF Response:", {
        status: pdfResponse.status,
        statusText: pdfResponse.statusText,
        contentType: pdfResponse.headers.get("content-type"),
        url: pdfResponse.url,
      });
      console.log("ðŸ”µ JSON Response:", {
        status: jsonResponse.status,
        statusText: jsonResponse.statusText,
        contentType: jsonResponse.headers.get("content-type"),
        url: jsonResponse.url,
      });

      if (!pdfResponse.ok || !jsonResponse.ok) {
        console.error(
          "âŒ Fetch failed:",
          pdfResponse.status,
          jsonResponse.status,
        );
        throw new Error("Failed to fetch sample files");
      }

      // Get blobs with original types
      const pdfBlob = await pdfResponse.blob();
      const jsonBlob = await jsonResponse.blob();

      console.log(
        "ðŸ”µ Blob types from server:",
        "PDF:",
        pdfBlob.type,
        pdfBlob.size,
        "bytes",
        "JSON:",
        jsonBlob.type,
        jsonBlob.size,
        "bytes",
      );

      // Check what content we actually got
      const pdfPreview = await pdfBlob.slice(0, 200).text();
      const jsonPreview = await jsonBlob.slice(0, 200).text();

      console.log("ðŸ” PDF content preview (first 200 chars):", pdfPreview);
      console.log("ðŸ” JSON content preview (first 200 chars):", jsonPreview);

      // Use original filenames from config
      const pdfFilename = sample.files.pdfFilename;
      const jsonFilename = sample.files.jsonFilename;

      // Create File objects directly from blobs with correct MIME types
      // IMPORTANT: We create Files directly from the response blobs without intermediate wrapping
      // to preserve full file content and avoid streaming issues
      const pdfFile = new File([pdfBlob], pdfFilename, {
        type: "application/pdf",
      });
      const jsonFile = new File([jsonBlob], jsonFilename, {
        type: "application/json",
      });

      console.log("âœ… Created PDF file:", {
        name: pdfFile.name,
        type: pdfFile.type,
        size: pdfFile.size,
        isFile: pdfFile instanceof File,
        constructor: pdfFile.constructor.name,
      });
      console.log("âœ… Created JSON file:", {
        name: jsonFile.name,
        type: jsonFile.type,
        size: jsonFile.size,
        isFile: jsonFile instanceof File,
        constructor: jsonFile.constructor.name,
      });

      // Verify content
      const pdfStart = await pdfFile.slice(0, 5).text();
      const jsonStart = await jsonFile.slice(0, 10).text();
      console.log("ðŸ” PDF starts with:", pdfStart);
      console.log("ðŸ” JSON starts with:", jsonStart);

      if (!pdfStart.startsWith("%PDF")) {
        console.error(
          "âŒ PDF content verification failed - does not start with %PDF",
        );
        console.error("âŒ Instead starts with:", pdfStart);
        console.error(
          "âŒ This means the file at",
          sample.files.pdf,
          "is NOT a PDF file",
        );
        console.error(
          "âŒ It might be an HTML error page or the file is missing",
        );
        throw new Error(
          "Invalid PDF content - file does not start with %PDF. Check browser console for details.",
        );
      }

      // Add files
      onFilesAdded([pdfFile, jsonFile], true);
      setDemoDialogOpen(false);
      setSelectedSample(null);

      console.log("âœ… Sample files loaded successfully");
    } catch (error) {
      console.error("âŒ Failed to load sample files:", error);
      alert(`Failed to load sample files: ${error}`);
    }
  };

  /**
   * Load batch sample - loads all 3 PDFs + combined Policy.json
   * This demonstrates multi-policy batch processing
   */
  const handleLoadBatchSample = async () => {
    if (!batchConfig) {
      alert("Batch configuration not loaded");
      return;
    }

    try {
      console.log("Loading batch sample - 3 PDFs + Policy.json");

      // Fetch all PDF files
      const pdfPromises = batchConfig.pdfFiles.map(async (pdfConfig: any, index: number) => {
        console.log(`📥 Fetching PDF ${index + 1}:`, pdfConfig.path);
        const response = await fetch(pdfConfig.path);
        if (!response.ok)
          throw new Error(`Failed to fetch ${pdfConfig.filename}`);
        const blob = await response.blob();
        console.log(`✅ Fetched PDF ${index + 1}:`, {
          filename: pdfConfig.filename,
          blobSize: blob.size,
          blobType: blob.type,
        });
        // Create File directly from blob without intermediate wrapping
        const file = new File([blob], pdfConfig.filename, {
          type: "application/pdf",
        });
        console.log(`📄 Created File ${index + 1}:`, {
          name: file.name,
          size: file.size,
          type: file.type,
        });
        return file;
      });

      // Fetch JSON file
      const jsonResponse = await fetch(batchConfig.jsonFile.path);
      if (!jsonResponse.ok) throw new Error("Failed to fetch Policy.json");
      const jsonBlob = await jsonResponse.blob();
      // Create File directly from blob without intermediate wrapping
      const jsonFile = new File(
        [jsonBlob],
        batchConfig.jsonFile.filename,
        { type: "application/json" },
      );

      // Wait for all PDFs
      const pdfFiles = await Promise.all(pdfPromises);

      console.log("Loaded batch files:", {
        pdfs: pdfFiles.map((f) => f.name),
        json: jsonFile.name,
      });

      // Add files - PDFs first, then JSON last
      onFilesAdded([...pdfFiles, jsonFile], true);
      setDemoDialogOpen(false);

      console.log("Batch sample files loaded successfully");
    } catch (error) {
      console.error("Failed to load batch sample:", error);
      alert(`Failed to load batch sample: ${error}`);
    }
  };

  // Data Quality Validation Handler - LOCAL (no agent needed for structure validation)
  const handleValidateJSON = async (uploadFile: UploadFile) => {
    try {
      setValidatingFileId(uploadFile.id);
      setValidationDialogOpen(true);
      console.log("Validating JSON:", uploadFile.file.name);

      // Read JSON file content
      const fileContent = await uploadFile.file.text();

      // Parse to check basic JSON validity first
      let parsedJson: any;
      try {
        parsedJson = JSON.parse(fileContent);
      } catch (e) {
        setValidationResult(
          `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
POLICY JSON VALIDATION REPORT
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Status: FAIL
Confidence: 0%

Error: Invalid JSON format
${e instanceof Error ? e.message : "Could not parse JSON"}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Ready for Processing: NO
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
        );
        setValidatingFileId(null);
        return;
      }

      // Normalize to array
      const policies = Array.isArray(parsedJson) ? parsedJson : [parsedJson];
      const policyCount = policies.length;
      console.log(`Validating ${policyCount} policy(ies)`);

      // Required fields to check (removed appnum - not required anymore)
      const REQUIRED_ROOT_FIELDS = ["policyNumber", "entnum"];
      const REQUIRED_APP_FIELDS = [
        "policyNumber",
        "effectiveDate",
        "expirationDate",
      ];

      const errors: string[] = [];
      const warnings: string[] = [];
      const policiesDetected: string[] = [];
      const fieldChecks: { field: string; status: "found" | "missing" }[] = [];

      // Track which fields are found across all policies
      const fieldsFound = new Set<string>();

      policies.forEach((policy: any, index: number) => {
        const policyRef = policyCount > 1 ? `Policy #${index + 1}` : "Policy";

        // Check root level required fields
        REQUIRED_ROOT_FIELDS.forEach((field) => {
          if (policy[field]) {
            fieldsFound.add(field);
          } else {
            errors.push(`${policyRef}: Missing required field '${field}'`);
          }
        });

        // Check for jsonData
        if (!policy.jsonData) {
          errors.push(`${policyRef}: Missing 'jsonData' object`);
        } else {
          fieldsFound.add("jsonData");

          // Check for Application node
          if (!policy.jsonData.Application) {
            errors.push(`${policyRef}: Missing 'jsonData.Application' object`);
          } else {
            fieldsFound.add("Application node");

            // Check Application required fields
            REQUIRED_APP_FIELDS.forEach((field) => {
              if (policy.jsonData.Application[field]) {
                fieldsFound.add(field);
              } else {
                errors.push(`${policyRef}: Missing 'Application.${field}'`);
              }
            });
          }

          // Check for optional but recommended fields
          if (!policy.jsonData.GeneralInfo) {
            warnings.push(`${policyRef}: Missing 'GeneralInfo' (recommended)`);
          }
          if (!policy.jsonData.Summary) {
            warnings.push(
              `${policyRef}: Missing 'Summary' (recommended for premium data)`,
            );
          }
        }

        // Build policy detection string
        const appnum = policy.appnum || "N/A";
        const policyNum =
          policy.policyNumber ||
          policy.jsonData?.Application?.policyNumber ||
          "N/A";
        const program = policy.jsonData?.Application?.Program || "Unknown";
        const insured =
          policy.jsonData?.GeneralInfo?.entityFullName ||
          policy.jsonData?.SubmissionInfo?.txtInsuredName ||
          policy.jsonData?.Application?.InsuredFullName ||
          "Unknown";
        policiesDetected.push(
          `${appnum} – ${policyNum} – ${program} (${insured})`,
        );
      });

      // Build field check results
      [
        ...REQUIRED_ROOT_FIELDS,
        "jsonData",
        "Application node",
        ...REQUIRED_APP_FIELDS,
      ].forEach((field) => {
        fieldChecks.push({
          field,
          status: fieldsFound.has(field) ? "found" : "missing",
        });
      });

      // Calculate confidence
      const totalChecks = fieldChecks.length * policyCount;
      const passedChecks = totalChecks - errors.length;
      const confidence =
        totalChecks > 0 ? Math.round((passedChecks / totalChecks) * 100) : 0;

      // Determine status
      const status = errors.length === 0 ? "PASS" : "FAIL";
      const readyForProcessing = errors.length === 0 ? "YES" : "NO";

      // Build the validation report
      let report = `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
POLICY JSON VALIDATION REPORT
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Status: ${status}
Confidence: ${confidence}%
Policies Found: ${policyCount}

Policies Detected:
${policiesDetected.map((p) => `• ${p}`).join("\n")}

Required Fields Check:
${fieldChecks.map((fc) => `${fc.status === "found" ? "✓" : "✗"} ${fc.field}: ${fc.status === "found" ? "Found" : "MISSING"}`).join("\n")}
`;

      if (errors.length > 0) {
        report += `
Errors (${errors.length}):
${errors.map((e) => `• ${e}`).join("\n")}
`;
      }

      if (warnings.length > 0) {
        report += `
Warnings (${warnings.length}):
${warnings.map((w) => `• ${w}`).join("\n")}
`;
      }

      report += `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Ready for Processing: ${readyForProcessing}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`;

      setValidationResult(report);
      console.log(
        `Validation complete: ${status}, ${errors.length} errors, ${warnings.length} warnings`,
      );
    } catch (error) {
      console.error("Validation error:", error);
      setValidationResult(
        `Validation Error\n\n${error instanceof Error ? error.message : "Unknown error occurred during validation."}`,
      );
    } finally {
      setValidatingFileId(null);
    }
  };

  /**
   * Handle file preview - opens dialog for JSON, new tab for PDF
   */
  const handlePreviewFile = async (uploadFile: UploadFile) => {
    const fileName = uploadFile.file.name.toLowerCase();
    const isPDF = fileName.endsWith(".pdf");
    const isJSON = fileName.endsWith(".json");

    if (isPDF) {
      // Debug logging for File object
      console.log("🔍 Previewing PDF:", {
        name: uploadFile.file.name,
        size: uploadFile.file.size,
        type: uploadFile.file.type,
        lastModified: uploadFile.file.lastModified,
        isFile: uploadFile.file instanceof File,
        isBlob: uploadFile.file instanceof Blob,
      });

      // Verify file has content
      if (uploadFile.file.size === 0) {
        console.error("❌ File has 0 bytes!");
        alert(`Cannot preview ${uploadFile.file.name}: File is empty`);
        return;
      }

      // For PDFs, open in a new browser tab
      const url = URL.createObjectURL(uploadFile.file);
      console.log("📎 Created blob URL:", url);
      window.open(url, "_blank");
      // Clean up the URL after a longer delay to ensure file loads completely
      // Increased to 30 seconds to handle larger files and slower browsers
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      return;
    }

    if (isJSON) {
      // For JSON, show in preview dialog with formatted view
      setPreviewFile(uploadFile);
      setPreviewLoading(true);
      setPreviewError(null);
      setPreviewDialogOpen(true);

      try {
        const text = await uploadFile.file.text();
        const parsed = JSON.parse(text);
        setPreviewContent(parsed);
      } catch (e) {
        console.error("Failed to parse JSON:", e);
        setPreviewError(
          `Failed to parse JSON: ${e instanceof Error ? e.message : "Invalid JSON format"}`,
        );
        setPreviewContent(null);
      } finally {
        setPreviewLoading(false);
      }
    }
  };

  const handleClosePreview = () => {
    setPreviewDialogOpen(false);
    setPreviewFile(null);
    setPreviewContent(null);
    setPreviewError(null);
  };

  /**
   * Render JSON tree recursively for preview
   */
  const renderJsonTree = (
    data: any,
    depth: number = 0,
    path: string = "",
  ): React.ReactNode => {
    if (data === null) return <span style={{ color: "#9e9e9e" }}>null</span>;
    if (data === undefined)
      return <span style={{ color: "#9e9e9e" }}>undefined</span>;

    if (typeof data === "boolean") {
      return <span style={{ color: "#f57c00" }}>{data.toString()}</span>;
    }

    if (typeof data === "number") {
      return <span style={{ color: "#1976d2" }}>{data}</span>;
    }

    if (typeof data === "string") {
      // Truncate long strings
      const displayStr =
        data.length > 100 ? data.substring(0, 100) + "..." : data;
      return <span style={{ color: "#388e3c" }}>"{displayStr}"</span>;
    }

    if (Array.isArray(data)) {
      if (data.length === 0) return <span style={{ color: "#666" }}>[]</span>;

      // For arrays, show count and first few items
      return (
        <Box component="span">
          <span style={{ color: "#666" }}>[</span>
          <span style={{ color: "#9e9e9e", fontSize: "11px", marginLeft: 4 }}>
            {data.length} item{data.length !== 1 ? "s" : ""}
          </span>
          <Box sx={{ pl: 2 }}>
            {data.slice(0, 3).map((item, index) => (
              <Box
                key={index}
                sx={{ display: "flex", alignItems: "flex-start" }}
              >
                <span style={{ color: "#666", marginRight: 4 }}>{index}:</span>
                {renderJsonTree(item, depth + 1, `${path}[${index}]`)}
              </Box>
            ))}
            {data.length > 3 && (
              <Typography
                variant="caption"
                sx={{ color: "#9e9e9e", fontStyle: "italic" }}
              >
                ... and {data.length - 3} more
              </Typography>
            )}
          </Box>
          <span style={{ color: "#666" }}>]</span>
        </Box>
      );
    }

    if (typeof data === "object") {
      const keys = Object.keys(data);
      if (keys.length === 0)
        return <span style={{ color: "#666" }}>{"{}"}</span>;

      return (
        <Box component="span">
          <span style={{ color: "#666" }}>{"{"}</span>
          <Box sx={{ pl: 2 }}>
            {keys.slice(0, 10).map((key) => (
              <Box
                key={key}
                sx={{ display: "flex", alignItems: "flex-start", mb: 0.25 }}
              >
                <span style={{ color: "#c62828", fontWeight: 500 }}>
                  "{key}"
                </span>
                <span style={{ color: "#666", margin: "0 4px" }}>:</span>
                {renderJsonTree(data[key], depth + 1, `${path}.${key}`)}
              </Box>
            ))}
            {keys.length > 10 && (
              <Typography
                variant="caption"
                sx={{ color: "#9e9e9e", fontStyle: "italic" }}
              >
                ... and {keys.length - 10} more keys
              </Typography>
            )}
          </Box>
          <span style={{ color: "#666" }}>{"}"}</span>
        </Box>
      );
    }

    return <span>{String(data)}</span>;
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          bgcolor: "#ffffff",
          p: 3,
          borderRadius: 4,
          boxShadow:
            "0 10px 15px -3px rgba(0, 0, 0, 0.12), 0 4px 6px -2px rgba(0, 0, 0, 0.08)",

          border: "1px solid #E5E7EB",
          "&:hover": {
            boxShadow:
              "0 10px 15px -3px rgba(0, 0, 0, 0.12), 0 4px 6px -2px rgba(0, 0, 0, 0.08)",
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <UploadIcon sx={{ color: "#1a56db", fontSize: 22 }} />
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 800, color: "#0F172A", letterSpacing: "-0.01em" }}
          >
            New Analysis
          </Typography>
        </Box>

        {/* Dropzone */}
        <Box
          {...getRootProps()}
          sx={{
            border: "1px dashed #E2E8F0",
            borderRadius: 3,
            p: 4,
            textAlign: "center",
            bgcolor: "white",
            cursor: isUploading ? "not-allowed" : "pointer",
            transition: "all 0.2s ease",
            boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
            "&:hover": {
              borderColor: "primary.main",
              bgcolor: "#F8FAFC",
            },
          }}
        >
          <input {...getInputProps()} />
          <Box>
            <InsertDriveFileIcon sx={{ color: "#94A3B8", fontSize: 30 }} />
          </Box>
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, mb: 0.5, color: "#0F172A" }}
          >
            Drag & drop <br></br>
            <span style={{ fontWeight: 700, color: "#0F172A" }}>
              LossRun PDF
            </span>{" "}
            or{" "}
            <span style={{ fontWeight: 700, color: "#0F172A" }}>
              Policy JSON
            </span>
          </Typography>
          <Typography variant="caption" sx={{ color: "#94A3B8" }}>
            or click to browse
          </Typography>
        </Box>

        {/* Demo Samples */}

        {/* File List */}
        <Box
          sx={{
            maxHeight: 300,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
          }}
        >
          {files.length === 0 ? (
            <Box
              sx={{
                py: 4,
                textAlign: "center",
                border: "1px dashed #E2E8F0",
                borderRadius: 3,
                bgcolor: "#F8FAFC",
              }}
            >
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                No files selected for analysis.
              </Typography>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    color: "text.secondary",
                    letterSpacing: "0.05em",
                  }}
                >
                  READY TO PROCESS ({files.length})
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "error.main",
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                  onClick={() => files.forEach((f) => onFileRemove(f.id))}
                >
                  Clear all
                </Typography>
              </Box>

              <List
                sx={{
                  p: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  overflowY: "auto",
                }}
              >
                {[...files]
                  .sort((a, b) => {
                    const aIsPdf = a.file.name.toLowerCase().endsWith(".pdf");
                    const bIsPdf = b.file.name.toLowerCase().endsWith(".pdf");
                    if (aIsPdf && !bIsPdf) return -1; // PDF comes first
                    if (!aIsPdf && bIsPdf) return 1; // JSON comes second
                    return 0; // Keep original order within same type
                  })
                  .map((uploadFile) => {
                    const fileName = uploadFile.file.name.toLowerCase();
                    const isPDF = fileName.endsWith(".pdf");
                    const isJSON = fileName.endsWith(".json");

                    return (
                      <ListItem
                        key={uploadFile.id}
                        sx={{
                          p: 1,
                          border: "1px solid #F1F5F9",
                          borderRadius: 2,
                          bgcolor: "#FFFFFF",
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.08)",
                        }}
                      >
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 1,
                            bgcolor: isPDF ? "#FEF2F2" : "#F0F9FF",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          {isPDF ? (
                            <PdfIcon sx={{ fontSize: 18, color: "#EF4444" }} />
                          ) : (
                            <FileIcon sx={{ fontSize: 18, color: "#0EA5E9" }} />
                          )}
                        </Box>
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: "200px",
                                  color: "#0F172A",
                                }}
                              >
                                {uploadFile.file.name}
                              </Typography>
                              <Chip
                                label={isPDF ? "PDF" : "JSON"}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: "0.65rem",
                                  fontWeight: 700,
                                  bgcolor: isPDF ? "#FEE2E2" : "#E0F2FE",
                                  color: isPDF ? "#991B1B" : "#075985",
                                }}
                              />
                            </Box>
                          }
                          sx={{ m: 0, flex: 1, minWidth: 0 }}
                        />

                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          {isJSON && (
                            <Tooltip title="Validate JSON Structure">
                              <IconButton
                                size="small"
                                onClick={() => handleValidateJSON(uploadFile)}
                                sx={{
                                  color: "#64748B",
                                  mr: 0.5,
                                  "&:hover": {
                                    color: "primary.main",
                                    bgcolor: "primary.50",
                                  },
                                }}
                              >
                                {validatingFileId === uploadFile.id ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <ValidateIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}

                          <Tooltip title="Preview File">
                            <IconButton
                              size="small"
                              onClick={() => handlePreviewFile(uploadFile)}
                              sx={{
                                color: "#64748B",
                                mr: 0.5,
                                "&:hover": {
                                  color: "primary.main",
                                  bgcolor: "primary.50",
                                },
                              }}
                            >
                              <PreviewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <IconButton
                            size="small"
                            onClick={() => onFileRemove(uploadFile.id)}
                            sx={{
                              color: "#94A3B8",
                              "&:hover": {
                                color: "error.main",
                                bgcolor: "error.50",
                              },
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </ListItem>
                    );
                  })}
              </List>
            </>
          )}
        </Box>

        {/* Error Display */}
        {error && (
          <Alert
            severity="error"
            icon={false}
            sx={{ borderRadius: 2, "& .MuiAlert-message": { p: 0 } }}
          >
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {error}
            </Typography>
          </Alert>
        )}

        {/* Submit Button */}
        <Button
          variant="contained"
          size="large"
          onClick={onSubmit}
          disabled={files.length === 0 || isUploading}
          fullWidth
          startIcon={!isUploading && <RunIcon />}
          sx={{
            py: 1.5,
            borderRadius: "10px",
            fontWeight: 700,
            textTransform: "none",
            bgcolor: "primary.main",
            boxShadow: "none",
            "&:hover": {
              bgcolor: "primary.dark",
              boxShadow: "0 4px 12px rgba(5, 88, 253, 0.2)",
            },
            "&.Mui-disabled": {
              bgcolor: "#E2E8F0",
              color: "#94A3B8",
            },
          }}
        >
          {isUploading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Run Analysis"
          )}
        </Button>

        {/* Demo Samples Trigger at Bottom */}

        {/* Demo Samples Dialog */}
        <Dialog
          open={demoDialogOpen}
          onClose={() => {
            setDemoDialogOpen(false);
            setSelectedSample(null);
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <DemoIcon sx={{ color: "#1a56db" }} />
                <Typography variant="h6">Demo Samples</Typography>
              </Box>
              <IconButton onClick={() => setDemoDialogOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent dividers>
            {selectedSample ? (
              // Sample Details View
              <Box>
                <Button
                  size="small"
                  onClick={() => setSelectedSample(null)}
                  sx={{ mb: 2 }}
                >
                  â† Back to samples
                </Button>

                <Typography variant="h6" gutterBottom sx={{ fontWeight: 800, color: "#0F172A" }}>
                  {selectedSample.name}
                </Typography>

                <Chip
                  label={selectedSample.category}
                  size="small"
                  sx={{
                    mb: 2,
                    bgcolor: "primary.main",
                    color: "white",
                    fontWeight: 800,
                    fontSize: "0.7rem",
                    height: 24,
                  }}
                />

                <Typography
                  variant="body2"
                  paragraph
                  sx={{ fontStyle: "italic" }}
                >
                  {selectedSample.description}
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700, color: "primary.main", mb: 1.5 }}>
                  Policy Details
                </Typography>
                <Box sx={{ mb: 2, pl: 2 }}>
                  <Typography variant="body2">
                    <strong>Insured:</strong>{" "}
                    {selectedSample.policyDetails.insured}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Location:</strong>{" "}
                    {selectedSample.policyDetails.location}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Policy #:</strong>{" "}
                    {selectedSample.policyDetails.policyNumber}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Program:</strong>{" "}
                    {selectedSample.policyDetails.program}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Employees:</strong>{" "}
                    {selectedSample.policyDetails.employees}
                  </Typography>
                  <Typography variant="body2">
                    <strong>General Liability:</strong>{" "}
                    {selectedSample.policyDetails.coverages.generalLiability}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Property:</strong>{" "}
                    {selectedSample.policyDetails.coverages.property}
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700, color: "primary.main", mb: 1.5 }}>
                  Claims Summary
                </Typography>
                <Box sx={{ pl: 2 }}>
                  <Typography variant="body2">
                    <strong>Total Claims:</strong>{" "}
                    {selectedSample.claimsSummary.totalClaims}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Total Incurred:</strong>{" "}
                    {selectedSample.claimsSummary.totalIncurred}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Loss Ratio:</strong>{" "}
                    {selectedSample.claimsSummary.lossRatio}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Claim Type:</strong>{" "}
                    {selectedSample.claimsSummary.claimType}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Description:</strong>{" "}
                    {selectedSample.claimsSummary.description}
                  </Typography>
                </Box>
              </Box>
            ) : (
              // Sample List View
              <Box>
                {/* Batch Mode Option */}
                <Box
                  sx={{
                    mb: 3,
                    p: 3,
                    bgcolor: "#F8FAFC",
                    borderRadius: 3,
                    border: "1px solid #E2E8F0",
                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "4px",
                      height: "100%",
                      bgcolor: "primary.main",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mb: 1.5,
                    }}
                  >
                    <Chip
                      label="BATCH MODE"
                      size="small"
                      sx={{
                        bgcolor: "#1a56db",
                        color: "white",
                        fontWeight: 800,
                        fontSize: "0.65rem",
                        height: 22,
                      }}
                    />
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 800, color: "#0F172A" }}
                    >
                      Multi-Policy Demo
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Load all 2 sample PDFs + combined Policy.json to demonstrate
                    batch processing of multiple policies in a single
                    submission.
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mb: 1.5,
                    }}
                  >
                    <Chip
                      label="2 PDFs"
                      size="small"
                      sx={{
                        bgcolor: "#FEF2F2",
                        color: "#991B1B",
                        fontWeight: 700,
                        border: "1px solid #FEE2E2",
                      }}
                    />
                    <Chip
                      label="1 Policy.json"
                      size="small"
                      sx={{
                        bgcolor: "#F0F9FF",
                        color: "#075985",
                        fontWeight: 700,
                        border: "1px solid #E0F2FE",
                      }}
                    />

                  </Box>
                  <Button
                    variant="contained"
                    onClick={handleLoadBatchSample}
                    startIcon={<UploadIcon />}
                    fullWidth
                    sx={{
                      py: 1.25,
                      borderRadius: 2,
                      fontWeight: 700,
                      textTransform: "none",
                      boxShadow: "none",
                      "&:hover": {
                        bgcolor: "primary.dark",
                        boxShadow: "0 4px 12px rgba(5, 88, 253, 0.2)",
                      },
                    }}
                  >
                    Load Batch Sample (2 Policies)
                  </Button>
                </Box>

                <Divider sx={{ my: 2 }}>
                  <Chip label="OR SELECT SINGLE POLICY" size="small" />
                </Divider>

                <List sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {samples.map((sample, index) => (
                    <ListItem
                      key={sample.id}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        p: 2,
                        borderRadius: 3,
                        border: "1px solid #F1F5F9",
                        bgcolor: "#FFFFFF",
                        cursor: "default",
                        transition: "all 0.2s ease",
                        boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.04)",
                        "&:hover": {
                          borderColor: "primary.main",
                          bgcolor: "#F8FAFC",
                          boxShadow: "0px 4px 12px rgba(5, 88, 253, 0.1)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          // mb: 1.5,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: 1,
                              bgcolor: "rgba(5, 88, 253, 0.08)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <FileIcon sx={{ color: "#1a56db", fontSize: 18 }} />
                          </Box>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 700, color: "#0F172A" }}
                          >
                            {sample.name}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', minWidth: 100 }}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleLoadSample(sample);
                            }}
                            startIcon={<UploadIcon sx={{ fontSize: '1rem !important' }} />}
                            sx={{
                              py: 0.5,
                              px: 1.5,
                              fontSize: '0.725rem',
                              fontWeight: 700,
                              textTransform: 'none',
                              borderRadius: 1.5,
                              boxShadow: 'none',
                              whiteSpace: 'nowrap',
                              '&:hover': {
                                bgcolor: 'primary.dark',
                                boxShadow: '0 4px 12px rgba(5, 88, 253, 0.2)'
                              }
                            }}
                          >
                            Load Sample
                          </Button>
                        </Box>
                      </Box>
                      {/* <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
                        <Chip
                          label={sample.category}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "0.65rem",
                            fontWeight: 700,
                            bgcolor: "#F1F5F9",
                            color: "#475569",
                          }}
                        />
                      </Box> */}
                      <Typography
                        variant="body2"
                        sx={{ color: "#64748B", lineHeight: 1.5 }}
                      >
                        {sample.description}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </DialogContent>

          <DialogActions>
            {selectedSample && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleLoadSample(selectedSample)}
                startIcon={<UploadIcon />}
              >
                Load This Sample
              </Button>
            )}
            <Button
              onClick={() => {
                setDemoDialogOpen(false);
                setSelectedSample(null);
              }}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                color: "text.secondary",
              }}
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

        {/* Data Quality Validation Result Dialog */}
        <Dialog
          open={validationDialogOpen}
          onClose={() => setValidationDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: "white",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              bgcolor: "#f5f7fa",
              borderBottom: "2px solid #1a73e8",
              py: 2,
            }}
          >
            <MagicWandIcon sx={{ color: "#1a73e8", fontSize: 28 }} />
            <Typography
              variant="h6"
              component="span"
              sx={{ flexGrow: 1, fontWeight: 600, color: "#2c3e50" }}
            >
              Data Quality Validation
            </Typography>
            <IconButton
              onClick={() => setValidationDialogOpen(false)}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent
            dividers
            sx={{
              bgcolor: "white",
              p: 0,
            }}
          >
            {validatingFileId ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                  py: 4,
                }}
              >
                <CircularProgress size={48} color="primary" />
                <Typography variant="body1" color="text.secondary">
                  Analyzing policy structure...
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
                  fontSize: "14px",
                  lineHeight: 1.5,
                  color: "#2c3e50",
                  maxHeight: "60vh",
                  overflow: "auto",
                  p: 2,
                  bgcolor: "#f8f9fa",
                }}
              >
                {validationResult
                  .split("\n")
                  .map((line, index) => {
                    const trimmedLine = line.trim();

                    // Main title (first line typically)
                    if (index === 0 && trimmedLine.match(/^[A-Z][A-Z\s]+$/)) {
                      return (
                        <Typography
                          key={index}
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            fontSize: "18px",
                            color: "#1a73e8",
                            mb: 2,
                            pb: 1,
                            borderBottom: "2px solid #1a73e8",
                          }}
                        >
                          {trimmedLine}
                        </Typography>
                      );
                    }

                    // Section headers (ALL CAPS ending with colon)
                    const headerMatch =
                      trimmedLine.match(/^([A-Z][A-Z\s&]+):$/);
                    if (headerMatch) {
                      return (
                        <Typography
                          key={index}
                          sx={{
                            fontWeight: 700,
                            fontSize: "15px",
                            color: "#2c3e50",
                            mt: 2.5,
                            mb: 1,
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          {headerMatch[1]}
                        </Typography>
                      );
                    }

                    // Status/Confidence/Key metrics (label: value format)
                    const metricMatch = trimmedLine.match(
                      /^([A-Z][A-Za-z\s]+):\s*(.+)$/,
                    );
                    if (metricMatch && !line.startsWith("  ")) {
                      const [, label, value] = metricMatch;
                      return (
                        <Box key={index} sx={{ display: "flex", mb: 0.5 }}>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              minWidth: "180px",
                              color: "#5f6368",
                            }}
                          >
                            {label}:
                          </Typography>
                          <Typography sx={{ color: "#2c3e50" }}>
                            {value}
                          </Typography>
                        </Box>
                      );
                    }

                    // Subsection headers (Title Case, not indented)
                    if (
                      trimmedLine &&
                      !line.startsWith("  ") &&
                      trimmedLine.match(/^[A-Z][a-z]/)
                    ) {
                      return (
                        <Typography
                          key={index}
                          sx={{
                            fontWeight: 600,
                            fontSize: "14px",
                            color: "#444",
                            mt: 1.5,
                            mb: 0.5,
                          }}
                        >
                          {trimmedLine}
                        </Typography>
                      );
                    }

                    // Indented detail lines
                    if (line.startsWith("  ") && trimmedLine) {
                      return (
                        <Typography
                          key={index}
                          sx={{
                            fontSize: "13px",
                            color: "#5f6368",
                            pl: 3,
                            py: 0.25,
                          }}
                        >
                          • {trimmedLine}
                        </Typography>
                      );
                    }

                    // Regular lines
                    if (trimmedLine) {
                      return (
                        <Typography
                          key={index}
                          sx={{ fontSize: "14px", color: "#2c3e50", mb: 0.5 }}
                        >
                          {trimmedLine}
                        </Typography>
                      );
                    }

                    // Skip excessive blank lines
                    return null;
                  })
                  .filter(Boolean)}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ bgcolor: "white", px: 3, py: 2 }}>
            <Button
              onClick={() => setValidationDialogOpen(false)}
              variant="contained"
              disabled={validatingFileId !== null}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* JSON File Preview Dialog */}
        <Dialog
          open={previewDialogOpen}
          onClose={handleClosePreview}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: "white",
              maxHeight: "80vh",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              bgcolor: "#f5f7fa",
              borderBottom: "2px solid #1976d2",
              py: 2,
            }}
          >
            <PreviewIcon sx={{ color: "#1976d2", fontSize: 28 }} />
            <Box sx={{ flexGrow: 1 }}>
              <Typography
                variant="h6"
                component="span"
                sx={{ fontWeight: 600, color: "#2c3e50" }}
              >
                File Preview
              </Typography>
              {previewFile && (
                <Typography
                  variant="caption"
                  display="block"
                  sx={{ color: "#5f6368" }}
                >
                  {previewFile.file.name} (
                  {(previewFile.file.size / 1024).toFixed(1)} KB)
                </Typography>
              )}
            </Box>
            <IconButton onClick={handleClosePreview} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent
            dividers
            sx={{
              bgcolor: "#fafafa",
              p: 0,
            }}
          >
            {previewLoading ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                  py: 4,
                }}
              >
                <CircularProgress size={48} color="primary" />
                <Typography variant="body1" color="text.secondary">
                  Loading file preview...
                </Typography>
              </Box>
            ) : previewError ? (
              <Alert severity="error" sx={{ m: 2 }}>
                {previewError}
              </Alert>
            ) : previewContent ? (
              <Box sx={{ p: 2 }}>
                {/* Summary Stats */}
                {Array.isArray(previewContent) ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>
                        Array with {previewContent.length} item(s)
                      </strong>
                      {previewContent.length > 0 &&
                        previewContent[0].appnum && (
                          <span>
                            {" "}
                            — AppNums:{" "}
                            {previewContent
                              .map((p: any) => p.appnum)
                              .join(", ")}
                          </span>
                        )}
                    </Typography>
                  </Alert>
                ) : (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>
                        Object with {Object.keys(previewContent).length} key(s)
                      </strong>
                      {previewContent.appnum && (
                        <span> — AppNum: {previewContent.appnum}</span>
                      )}
                    </Typography>
                  </Alert>
                )}

                {/* JSON Tree View */}
                <Box
                  sx={{
                    bgcolor: "white",
                    p: 2,
                    borderRadius: 1,
                    border: "1px solid #e0e0e0",
                    fontFamily: "monospace",
                    fontSize: "13px",
                    lineHeight: 1.6,
                    maxHeight: "50vh",
                    overflow: "auto",
                  }}
                >
                  {renderJsonTree(previewContent)}
                </Box>

                {/* Raw JSON toggle */}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    💡 This is a structured preview. The full JSON will be used
                    for processing.
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography color="text.secondary">
                  No content to preview
                </Typography>
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ bgcolor: "white", px: 3, py: 2 }}>
            <Button onClick={handleClosePreview} variant="contained">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
      <Button
        variant="outlined"
        fullWidth
        startIcon={<DemoIcon sx={{ color: "#1a56db" }} />}
        onClick={() => setDemoDialogOpen(true)}
        // disabled={isUploading}
        sx={{
          mt: 2,
          color: "primary.main",
          borderColor: "rgba(5, 88, 253, 0.3)",
          textTransform: "none",
          fontWeight: 700,
          borderRadius: "10px",
          bgcolor: "white",
          py: 1,
          "&:hover": {
            borderColor: "primary.main",
            bgcolor: "rgba(5, 88, 253, 0.04)",
            boxShadow: "0 2px 8px rgba(5, 88, 253, 0.08)",
          },
        }}
      >
        Try Demo Samples
      </Button>
    </>
  );
};

export default UploadManager;
